import React, { ReactNode } from 'react'

import {
  BreadcrumbsProps,
  TBreadcrumbItem
} from 'components_new/molecules/Breadcrumbs'

import Button from 'components_new/atoms/Button'

import GroupAbout from 'components_new/organisms/GroupAbout'
import GroupNavigation from 'components_new/organisms/GroupNavigation'

import PageLayout, { PageLayoutProps } from '../PageLayout'

import { useGroup, useMember } from 'redux/hooks/Groups'
import { useParams } from 'utils/hooks'
import { useTranslation } from 'language/translator'
import { useGroupMemberActions } from 'redux/actions/GroupMembers'
import { useGroupActions } from 'redux/actions/Groups'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

interface GroupTemplateProps {
  children: ReactNode
  topbar?: Pick<NonNullable<PageLayoutProps['topbar']>, 'actions'>
  variant?: PageLayoutProps['variant']
  subpage?: TBreadcrumbItem
}

const GroupTemplate = (props: GroupTemplateProps) => {
  const { children, subpage, topbar, variant = 'feed' } = props

  const { id } = useParams()
  const { t } = useTranslation()
  const { tryDeleteGroupMember } = useGroupMemberActions()
  const { tryInviteToGroup } = useGroupActions()

  const group = useGroup(id)
  const groupMember = useMember(id)
  const { account, loading: accountLoading } = useAccountSettings()

  const breadcrumbs: BreadcrumbsProps['items'] = [
    { href: '/groups', icon: { name: 'Groups' }, title: t('global', 'groups') }
  ]

  if (!group || accountLoading) return null // @TODO

  if (group) {
    breadcrumbs.push({
      href: `/groups/${group.id}`,
      title: group.name
    })
  }

  if (subpage) {
    breadcrumbs.push(subpage)
  }

  const handleJoinGroup = () => {
    tryInviteToGroup(group.id, account.id, group.is_private, false)
  }

  const handleLeaveGroup = () => {
    if (group && groupMember) {
      tryDeleteGroupMember(groupMember.id, group.id, true)
    }
  }

  const leaveIsDisabled =
    Boolean(groupMember) &&
    group.owners.length === 1 &&
    group.owners[0].id === groupMember?.id

  return (
    <>
      <PageLayout
        rightSidebar={{
          content: (
            <>
              <GroupNavigation sx={{ mb: 2 }} />
              <GroupAbout />
            </>
          ),
          size: 'medium'
        }}
        topbar={{
          actions: (
            <>
              {!groupMember && !group.is_private ? (
                <Button onClick={handleJoinGroup}>{t('global', 'join')}</Button>
              ) : null}
              {topbar?.actions}
            </>
          ),
          breadcrumbs: breadcrumbs,
          mobile: { goBack: true },
          options: groupMember
            ? [
                {
                  disabled: leaveIsDisabled,
                  iconName: 'LogoutOutlined',
                  onClick: handleLeaveGroup,
                  title: t('global', 'leave'),
                  tooltip: leaveIsDisabled
                    ? t('groups', 'one_owner_required_tooltip')
                    : undefined
                }
              ]
            : undefined
        }}
        variant={variant}
      >
        {children}
      </PageLayout>
    </>
  )
}

export default GroupTemplate
